import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DbSizes, OrphanFiles } from '@/components/admin/DbStorageTools';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KIND = {
  used: { ar: 'مستخدمة', sv: 'Används', cls: 'bg-emerald-50 text-emerald-700' },
  log: { ar: 'سجل', sv: 'Logg', cls: 'bg-sky-50 text-sky-700' },
  backup: { ar: 'نسخة قديمة – ستُحذف', sv: 'Gammal kopia – raderas', cls: 'bg-rose-50 text-rose-700' },
  kept_copy: { ar: 'نسخة مستخدمين – تبقى', sv: 'Användarkopia – behålls', cls: 'bg-amber-50 text-amber-700' },
};
const fmt = (n) => Number(n || 0).toLocaleString('sv-SE');

export default function DatabaseCleanup({ token, i18n }) {
  const ar = i18n.language === 'ar';
  const t = (a, s) => (ar ? a : s);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/db-cleanup/preview`, { headers: { Authorization: `Bearer ${token}` } });
    setPlan(data);
  }, [token]);
  useEffect(() => { load().catch(() => toast.error('Error')); }, [load]);

  const run = async () => {
    const oldLogs = plan.logs.reduce((a, l) => a + l.old, 0);
    const summary = [
      t(`سيتم حذف ${plan.drop.length} نسخة قديمة:`, `${plan.drop.length} gamla kopior raderas:`),
      ...plan.drop.map((r) => `  - ${r.name} (${fmt(r.count)})`),
      t('تبقى نسخ المستخدمين:', 'Användarkopior behålls:'),
      ...plan.kept_copies.map((r) => `  - ${r.name} (${fmt(r.count)})`),
      t(`سجلات قديمة ستُحذف: ${fmt(oldLogs)}`, `Gamla loggposter raderas: ${fmt(oldLogs)}`),
      ...plan.logs.map((l) => `  - ${l.name} (${l.days} ${t('يوماً', 'dagar')}): -${fmt(l.old)} / ${t('يبقى', 'kvar')} ${fmt(l.keep)}`),
      '', t('الحذف نهائي. اكتب "احذف" للتأكيد:', 'Raderingen är permanent. Skriv "RADERA" för att bekräfta:'),
    ].join('\n');
    const word = t('احذف', 'RADERA');
    if ((window.prompt(summary) || '').trim() !== word) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/db-cleanup/run`, { confirm: 'DELETE' }, { headers });
      setReport(data);
      toast.success(t('تم التنظيف', 'Rensning klar'));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };

  if (!plan) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const oldLogs = plan.logs.reduce((a, l) => a + l.old, 0);
  const nothing = plan.drop.length === 0 && oldLogs === 0;

  return (
    <div className="space-y-5" dir={ar ? 'rtl' : 'ltr'} data-testid="db-cleanup">
      <div className="flex flex-wrap items-center gap-3">
        <Database className="w-6 h-6 text-slate-600" />
        <h2 className="text-xl font-bold flex-1">{t('تنظيف قاعدة البيانات', 'Databasrensning')}</h2>
        <Button variant="outline" size="sm" onClick={() => load()} data-testid="db-cleanup-refresh"><RefreshCw className="w-4 h-4" /></Button>
        <Button onClick={run} disabled={busy || nothing} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="db-cleanup-run">
          {busy ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Trash2 className="w-4 h-4 me-2" />}{t('تنظيف', 'Rensa')}
        </Button>
      </div>
      <p className="text-sm text-slate-600" data-testid="db-cleanup-summary">
        {t(`سيُحذف ${plan.drop.length} نسخة قديمة و${fmt(oldLogs)} سجل قديم (زيارات الصفحات ونشاط المستخدمين: أقدم من 30 يوماً، باقي السجلات: أقدم من ${plan.retention_days} يوماً). السجلات القديمة تُحذف تلقائياً كل يوم.`,
          `${plan.drop.length} gamla kopior och ${fmt(oldLogs)} gamla loggposter raderas (sidbesök och användaraktivitet: äldre än 30 dagar, övriga: äldre än ${plan.retention_days} dagar). Gamla loggar rensas automatiskt varje dag.`)}
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <DbSizes token={token} t={t} />
        <OrphanFiles token={token} t={t} />
      </div>
      {report && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="db-cleanup-report">
          <p className="font-bold text-emerald-800">{t('تقرير التنظيف', 'Rapport')}</p>
          <p>{t('نسخ محذوفة:', 'Raderade kopior:')} {report.dropped.length} ({report.dropped.map((r) => r.name).join(', ') || '—'})</p>
          <p>{t('سجلات محذوفة:', 'Raderade loggposter:')} {Object.entries(report.trimmed).map(([k, v]) => `${k}: ${fmt(v)}`).join(' · ')}</p>
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm" data-testid="db-cleanup-table">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr><th className="p-2 text-start">{t('المجموعة', 'Samling')}</th><th className="p-2 text-end">{t('السجلات', 'Poster')}</th><th className="p-2 text-start">{t('الحالة', 'Status')}</th></tr>
          </thead>
          <tbody>
            {plan.collections.map((r) => {
              const log = plan.logs.find((l) => l.name === r.name);
              return (
                <tr key={r.name} className="border-t" data-testid={`db-row-${r.name}`}>
                  <td className="p-2 font-mono text-xs break-all">{r.name}</td>
                  <td className="p-2 text-end tabular-nums">{fmt(r.count)}</td>
                  <td className="p-2">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${KIND[r.kind].cls}`}>{t(KIND[r.kind].ar, KIND[r.kind].sv)}</span>
                    {log && log.old > 0 && <span className="ms-2 text-xs text-rose-600">-{fmt(log.old)} {t('قديم', 'gamla')}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
